import React from 'react';
import { Box, ButtonBase, Typography, useTheme } from '@mui/material';
import HomeIcon from '@mui/icons-material/Dashboard';
import TimeIcon from '@mui/icons-material/AccessTime';
import AwayIcon from '@mui/icons-material/BeachAccess';
import MeIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';

const MIN_TAP_TARGET = 44;

export type MobileTab = 'home' | 'time' | 'away' | 'me';

interface MobileTabBarProps {
  /** The active tab (accent-toned), or null on routes that aren't a destination. */
  active: MobileTab | null;
  /** "Me" opens the account sheet rather than navigating (D6). */
  onMeClick: () => void;
}

interface TabItem {
  id: MobileTab;
  label: string;
  Icon: React.ElementType;
  /** Destination route, or `undefined` for the non-navigating "Me" tab. */
  path?: string;
}

// Home / Time / Away navigate to existing routes; the icons mirror the
// prototype's MobileTabBar (Icon.dashboard / .timesheet / .timeoff / .user) and
// match the canonical nav icons in nav.tsx. All three routes are `always` gated
// (no permission key), so no hasPermission filter is needed. "Me" carries no
// route — it opens the account sheet (D6).
const TABS: TabItem[] = [
  { id: 'home', label: 'Home', Icon: HomeIcon, path: '/dashboard' },
  { id: 'time', label: 'Time', Icon: TimeIcon, path: '/timesheets' },
  { id: 'away', label: 'Away', Icon: AwayIcon, path: '/team-absences' },
  { id: 'me', label: 'Me', Icon: MeIcon },
];

/**
 * The global bottom navigation bar (prototype `MobileTabBar`): Home / Time /
 * Away / Me. The active tab is accent-toned; Home/Time/Away navigate via the app
 * router (never `window.location`); "Me" opens the account sheet (D6). Respects
 * the iOS safe-area inset and keeps every tap target ≥ 44px. Rendered by the
 * Shell at phone widths; the parent owns the fixed positioning.
 */
const MobileTabBar: React.FC<MobileTabBarProps> = ({ active, onMeClick }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: 'background.paper',
        pt: 0.5,
        pb: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
      }}
    >
      {TABS.map(({ id, label, Icon, path }) => {
        const isActive = id === active;
        return (
          <ButtonBase
            key={id}
            aria-label={label}
            aria-current={isActive ? 'page' : undefined}
            aria-haspopup={id === 'me' ? 'dialog' : undefined}
            onClick={() => (path ? navigate(path) : onMeClick())}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.25,
              minHeight: MIN_TAP_TARGET,
              py: 0.5,
              color: isActive ? 'primary.main' : 'text.secondary',
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: -2,
              },
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: isActive ? 700 : 500, lineHeight: 1 }}>
              {label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default MobileTabBar;
